import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  DOCUMENT,
  Renderer2,
  effect, DestroyRef
} from '@angular/core';
import {AuthService, UrlService} from './app-shared';
import { SettingsService } from './app-shared/services/settings.service';

import { TranslateService } from '@ngx-translate/core';
import { mapTo, filter, map, switchMap } from 'rxjs';
import { fromEvent, merge, of, Observable } from 'rxjs';
import { locale, loadMessages } from 'devextreme/localization';
import ruMessages from 'devextreme/localization/messages/ru.json';
import huMessages from 'devextreme/localization/messages/hu.json';
import csMessages from 'devextreme/localization/messages/cs.json';
import ukMessages from 'devextreme/localization/messages/uk.json';

import { registerLocaleData} from '@angular/common';
import localeEn from '@angular/common/locales/en';

import { Router, NavigationEnd, ActivatedRouteSnapshot, ActivationEnd } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { StorageService } from './app-shared/services/storage.service';
import { GoogleAnalyticsService } from './app-shared/services/google-analytics.service';
import { environment } from '../environments/environment';
import { VersionHandlerService } from './app-shared/services/version-handler.service';
import { CountryService } from './app-shared/services/country.service';
import { InactivityService } from './app-shared/services/inactivity.service';
import { MetaTagService } from "./app-shared/services/metatag-service";
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';

declare let window;

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    standalone: false
})
export class AppComponent implements OnInit{

  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);

  config;
  online$: Observable<any>;
  isOnline: boolean;
  height = '100%';
  isMobile: boolean;
  reloadLocationPending: boolean;
  loadingSetting: boolean;
  apiYoutubeLoaded: boolean = false;
  isBrowser;

  private oldAreaClass = '';
  private areaClass = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => `${event.url.split('/')[1]}-area`)
    )
  );
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private settings: SettingsService,
    public translate: TranslateService,
    public router: Router,
    private title: Title,
    private storageService: StorageService,
    public ga: GoogleAnalyticsService,
    public versionHandlerService: VersionHandlerService,
    private authService: AuthService,
    private countryService: CountryService,
    private inactivityService: InactivityService,
    // need for init constructor UrlService
    private urlService: UrlService,
    private metaTagService: MetaTagService,
  ) {
    this.isBrowser = this.metaTagService.isBrowser();

    effect(() => this.handleAreaStyles());

    if (this.isBrowser) {
      this.versionHandlerService.loadVersionConfigFromUri(() => {
        this.storageService.addToLocalStorage(
          'Front-Version',
          environment.appVersion
        );
        this.versionHandlerService.startIntervalHandleAppVersion();
      });

      this.isMobile = this.settings.isMobileSize();
      this.settings.onResize$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((size) => {
          if (
            (this.isMobile && this.settings.isMapBoardSize()) ||
            (!this.isMobile && this.settings.isMobileSize())
          ) {
            this.reloadLocationPending = true;
            document.location.reload();
          }
        });
      loadMessages(ruMessages);
      loadMessages(huMessages);
      loadMessages(csMessages);
      loadMessages(ukMessages);
      this.online$ = merge(
        of(navigator.onLine),
        fromEvent(window, 'online').pipe(mapTo(true)),
        fromEvent(window, 'offline').pipe(mapTo(false))
      );
    }
    this.settings
      .initSettings(this.isBrowser)
      .pipe(
        switchMap((setting) => {
          return this.authService
            .getApplicationSettings()
            .pipe(map(() => setting));
        }),
        switchMap((setting) => {
          return this.countryService
            .getClientCountry()
            .pipe(map((country) => setting ));
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (setting) => {
          this.config = setting;
          this.loadingSetting = true;
          if(this.isBrowser) {
            if (setting.ID_GTAG) {
              this.initGoogleTagManager(setting.ID_GTAG);
            }
            const setLang = this.storageService.getFromLocalStorage('lang');
            const clientCountry = JSON.parse(this.storageService.getFromLocalStorage('clientCountry'));
            if (setLang && setLang !== 'null') {
              this.translate.setDefaultLang(setLang);
              locale(this.settings.getLangForLocale(setLang));
            } else {
              const defaultLang = clientCountry && setting.languages.some(lang => lang === clientCountry) ?
                clientCountry : setting.b2bLanguage;
              this.translate.setDefaultLang(defaultLang);
              this.storageService.addToLocalStorage('lang', defaultLang);
              locale(this.settings.getLangForLocale(defaultLang));
            }
            registerLocaleData(localeEn);
            translate.addLangs(setting.languages);
          }

        },
        (error) => {
          this.loadingSetting = true;
          console.error("Couldn't load configurations /web.config", error);
        }
      );

    this.router.events
      .pipe(
        filter((event) => event instanceof ActivationEnd || event instanceof NavigationEnd),
        map(() => this.getDeepestTitle(this.router.routerState.snapshot.root))
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((title) => {
          let titlePage = title;
        if (this.router.url === '/start' && this.config) {
          titlePage = 'Start';
          this.title.setTitle(this.config.projectName);
        } else {
          this.title.setTitle(title ? this.translate.instant(title) : '');
        }
        if (this.isBrowser){
          this.ga.eventEmitterGTM({
            event: 'Pageview',
            pagePath: this.router.url,
            pageTitle: titlePage,
          });
        }
      });
  }

  private getDeepestTitle(routeSnapshot: ActivatedRouteSnapshot) {
    let title = routeSnapshot.data ? routeSnapshot.data['title'] : '';
    if (routeSnapshot.firstChild) {
      title = this.getDeepestTitle(routeSnapshot.firstChild) || title;
    }
    return title;
  }

  private initGoogleTagManager(id: string) {
    window.addTag(window, document, 'script', 'dataLayer', id);
  }

  private initYoutubePlayer() {
    if (!this.apiYoutubeLoaded) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
      this.apiYoutubeLoaded = true;
    }
  }

  private handleAreaStyles(): void {
    const areaClass = this.areaClass();
    if (this.oldAreaClass) {
      this.renderer.removeClass(this.document.body, this.oldAreaClass);
    }
    if (areaClass) {
      this.renderer.addClass(this.document.body, areaClass);
    }
    this.oldAreaClass = areaClass ?? '';
  }

  ngOnInit(): void {
   if (this.isBrowser) {
     this.online$
       .pipe(takeUntilDestroyed(this.destroyRef))
       .subscribe((isOnline) => {
       this.isOnline = isOnline;
     });
     this.height = this.settings.isMobileSize() ? window.innerHeight + 'px' : '100%';
     if (this.settings.isMobileSize()) {
       this.settings.onResize$
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((size) => {
           this.height = document.documentElement.clientHeight + 'px';
         });
     }
     this.initYoutubePlayer();
   }

  }

}
